import { setupLayouts } from "virtual:generated-layouts";
import type { App } from "vue";

import type { RouteRecordRaw } from "vue-router/auto";

import { createRouter, createWebHistory } from "vue-router/auto";
import websocket from "@/websocket";
import { notifyBug } from "../../utils/notifyBug";
import { userStore } from "@/store/user";
import { useNotificationStore } from "@/store/notification";

function recursiveLayouts(route: RouteRecordRaw): RouteRecordRaw {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i]);

    return route;
  }

  return setupLayouts([route])[0];
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: "smooth", top: 60 };

    return { top: 0 };
  },
  // Adicionar redirects e rotas antes de aplicar layouts
  extendRoutes: (pages) => [
    ...[...pages].map((route) => recursiveLayouts(route)), // Rotas e layouts
    {
      path: "/",
      redirect: "/dashboard",
    },
    {
      path: "/logout",
      name: "logout",
      component: {
        beforeRouteEnter(to, from, next) {
          websocket.disconnect();
          localStorage.clear();
          next("/auth/login");
        },
      },
    },
  ],
});

// Mapeamento de rotas para módulos (para validação de permissões)
const routeModuleMap: Record<string, string> = {
  dashboard: "dashboard",
  calendar: "calendar",
  "patients-list": "patients",
  "cadastros-anamnesis": "anamnese",
  "cadastros-consultorios": "chairs",
  "cadastros-colaboradores": "professionals",
  "cadastros-fornecedores": "suppliers",
  "financeiro-accounts-receivable": "invoices",
  "financeiro-accounts-payable": "expenses",
  "financeiro-categories": "finance-category",
  "financeiro-cash-flow": "cashflow",
  "financeiro-bank-account": "accounts",
  specialties: "cost_reference",
  reports: "reports",
  "sysadmin-users": "users",
  "plans-management": "plans",
  "sysadmin-billing-dashboard": "billing",
  "sysadmin-subscriptions": "subscriptions",
  "sysadmin-notifications": "notification",
  "sysadmin-report-recipients": "report-recipients",
};

const getSysAccessLevel = (
  rawLevel: number | string | undefined,
  isSysAdmin: boolean
): number => {
  if (typeof rawLevel === "number") {
    return rawLevel > 0 ? rawLevel : isSysAdmin ? 4 : 0;
  }

  if (typeof rawLevel === "string") {
    const parsedLevel = Number(rawLevel);
    if (!Number.isNaN(parsedLevel) && parsedLevel > 0) {
      return parsedLevel;
    }

    return isSysAdmin ? 4 : 0;
  }

  return isSysAdmin ? 4 : 0;
};

const getAllowedSysAdminModules = (sysAccessLevel: number): string[] => {
  if (sysAccessLevel >= 4) {
    return [
      "users",
      "notification",
      "plans",
      "billing",
      "subscriptions",
      "report-recipients",
    ];
  }

  if (sysAccessLevel === 3) {
    return ["users", "notification", "plans"];
  }

  if (sysAccessLevel === 2) {
    return ["users", "notification"];
  }

  if (sysAccessLevel === 1) {
    return ["users"];
  }

  return [];
};

router.beforeEach((to, from, next) => {
  const userData = localStorage.getItem("user");
  const hasToken = userData ? JSON.parse(userData)?.token : null;
  const user = userStore();

  if (to.meta.requireAuth && !hasToken) {
    next("/auth/login");
    return;
  }

  if (to.meta.requireSysAdmin && !user.isSysAdmin) {
    // Redirecionar para dashboard se não for sysAdmin
    next("/dashboard");
    return;
  }

  if (to.meta.requireSysAdmin && user.isSysAdmin && to.name && typeof to.name === "string") {
    const module = routeModuleMap[to.name];
    const sysAccessLevel = getSysAccessLevel(
      user.userGetter?.sysAccessLevel,
      user.isSysAdmin
    );
    const allowedSysAdminModules = getAllowedSysAdminModules(sysAccessLevel);

    if (module && !allowedSysAdminModules.includes(module)) {
      const notificationStore = useNotificationStore();
      notificationStore.notifyFail({
        title: "Acesso negado",
        text: "Você não tem permissão para acessar este módulo.",
      });
      next("/dashboard");
      return;
    }
  }

  // Verificar permissões de módulo (exceto para rotas públicas e sysAdmin)
  if (hasToken && to.name && typeof to.name === "string") {
    const module = routeModuleMap[to.name];

    // Se a rota está mapeada para um módulo, verificar permissão
    // IMPORTANTE: Rotas sysAdmin não devem verificar permissões de módulo
    // IMPORTANTE: SysAdmin tem acesso total a tudo
    if (module && !to.meta.requireSysAdmin) {
      // Verificar permissão com logs detalhados
      const hasPermission = user.hasPermission(module, "read");

      // SysAdmin sempre tem acesso total, Owner sempre tem acesso aos módulos da empresa
      if (
        !user.isSysAdmin &&
        !user.isOwner &&
        !hasPermission
      ) {
        // Tentar atualizar o token antes de negar acesso (pode ter mudado o plano)
        if (user.permissionsLoaded) {
          return user
            .refreshToken()
            .then(() => {
              // Verificar novamente após atualizar token
              if (user.hasPermission(module, "read")) {
                next();
              } else {
                // Se ainda não tiver permissão, bloquear acesso
                const notificationStore = useNotificationStore();
                notificationStore.notifyFail({
                  title: "Acesso negado",
                  text: "Você não tem permissão para acessar este módulo.",
                });
                next("/dashboard");
              }
            })
            .catch((error) => {
              console.error("Erro ao atualizar token:", error);
              // Se falhar ao atualizar, bloquear acesso
              const notificationStore = useNotificationStore();
              notificationStore.notifyFail({
                title: "Acesso negado",
                text: "Você não tem permissão para acessar este módulo.",
              });
              next("/dashboard");
            });
        }

        // Usuário não tem permissão para acessar este módulo
        // Redirecionar para dashboard com mensagem
        const notificationStore = useNotificationStore();
        notificationStore.notifyFail({
          title: "Acesso negado",
          text: "Você não tem permissão para acessar este módulo.",
        });
        next("/dashboard");
        return;
      }
    }
  }

  // Se chegou até aqui, permitir acesso

  // Evitar loops de navegação - se já estamos na rota correta, não navegar novamente
  if (to.path === from.path && to.name === from.name) {
    next(false);
    return;
  }

  websocket.connect();
  if (to.fullPath === "/auth/confirm-code" && hasToken) {
    next("/dashboard");
  } else {
    next();
  }
});

router.onError((error) => {
  notifyBug(error);
});

export { router };

export default function (app: App) {
  app.use(router);
}
